import { useState, useEffect } from 'react';
import { Analytics } from '@/analytics/events';

interface WaitlistModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function WaitlistModal({ isOpen, onClose }: WaitlistModalProps) {
    const [submitted, setSubmitted] = useState(false);
    const [email, setEmail] = useState('');

    // Reset state when modal is opened
    useEffect(() => {
        if (isOpen) {
            setSubmitted(false);
            setEmail('');
        }
    }, [isOpen]);

    // Handle escape key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    // Prevent scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-navy/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 overflow-hidden transform transition-all text-center">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-slate-400 hover:text-navy transition"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div className={submitted ? 'hidden' : 'block'}>
                    <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-8 h-8 text-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                    </div>

                    <h2 className="text-3xl font-black text-navy mb-4">Join The Waitlist</h2>
                    <p className="text-slate-600 mb-8 leading-relaxed">
                        We'll be opening up more spots for Steadfast HSA soon.
                        Drop your email below and we'll let you know when it's your turn!
                    </p>

                    {/* Hidden iframe that the Zoho form targets, so it doesn't open a new tab */}
                    <iframe
                        name="_zcSignup"
                        id="_zcSignup"
                        style={{ display: 'none' }}
                        onLoad={() => {
                            // When the iframe finishes loading the URL, we assume success
                            // We set a small timeout just in case the first load event is empty
                            setTimeout(() => {
                                setSubmitted(true)
                                Analytics.emailCaptured('homepage-waitlist')
                            }, 250);
                        }}
                    ></iframe>

                    <form
                        method="POST"
                        action="https://isasy-zngp.maillist-manage.com/weboptin.zc"
                        target="_zcSignup"
                        className="space-y-4"
                        onSubmit={() => {
                            // Show success state optimistically, the iframe will catch the actual response silently
                            // setTimeout(() => setSubmitted(true), 100);
                        }}
                    >
                        {/* Hidden fields explicitly matching the raw HTML snippet */}
                        <input type="hidden" name="submitType" value="optinCustomView" />
                        <input type="hidden" name="emailReportId" value="" />
                        <input type="hidden" name="formType" value="QuickForm" />
                        <input type="hidden" name="zx" id="cmpZuid" value="1369ec659" />
                        <input type="hidden" name="zcvers" value="3.0" />
                        <input type="hidden" name="oldListIds" id="allCheckedListIds" value="" />
                        <input type="hidden" name="mode" id="mode" value="OptinCreateView" />
                        <input type="hidden" name="zcld" id="zcld" value="116747b8a49671d61" />
                        <input type="hidden" name="zctd" id="zctd" value="116747b8a49671b91" />
                        <input type="hidden" name="document_domain" id="document_domain" value="" />
                        <input type="hidden" name="zc_Url" id="zc_Url" value="isasy-zngp.maillist-manage.com" />
                        <input type="hidden" name="new_optin_response_in" id="new_optin_response_in" value="0" />
                        <input type="hidden" name="duplicate_optin_response_in" id="duplicate_optin_response_in" value="0" />
                        <input type="hidden" name="zc_trackCode" id="zc_trackCode" value="ZCFORMVIEW" />
                        <input type="hidden" name="zc_formIx" id="zc_formIx" value="3z23a654942e0897190cacfdadf46af6b2aa219e0ccca8ccccb0a8b0d7d141f197" />
                        <input type="hidden" name="viewFrom" id="viewFrom" value="URL_ACTION" />
                        <input type="hidden" name="scriptless" id="scriptless" value="yes" />

                        <div className="relative">
                            <input
                                type="email"
                                name="CONTACT_EMAIL"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="name@example.com"
                                className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy focus:border-transparent transition text-navy font-medium placeholder:text-gray-400"
                            />
                        </div>

                        <button
                            type="submit"
                            className="w-full bg-navy text-white font-bold py-4 rounded-lg shadow-lg hover:opacity-90 transition transform hover:-translate-y-0.5 active:translate-y-0"
                        >
                            Sign Up Now
                        </button>
                    </form>
                    <p className="mt-4 text-xs text-slate-400">
                        No spam. Unsubscribe at any time.
                    </p>
                </div>

                <div className={submitted ? 'py-8 block' : 'hidden'}>
                    <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                        <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-navy mb-2">You're on the list!</h3>
                    <p className="text-slate-600 mb-8">
                        Keep an eye on your inbox. We'll be in touch soon with your invite.
                    </p>
                    <button
                        onClick={onClose}
                        className="w-full bg-gray-100 text-slate-700 font-bold py-3 rounded-lg hover:bg-gray-200 transition"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
